export const RADIO_BUTTON = 'radiobutton';
export const CHECKBOX = 'checkbox';
export const QUANTITY = 'quantity';
export const FREEFORM = 'free_form';

function getCheckboxModValue(modValue) {
  if (['string', 'number'].includes(typeof modValue)) {
    return [modValue];
  }
  return Object.keys(modValue).filter(item => modValue[item]);
}

/**
 * Mutates trasnformedModifications to add checkbox field type values if they exist
 * @param {string} modName - modifier name
 * @param {string} modValue - modifier value
 * @param {object} transformedModifications - object of all modified fields
 */
function updateCheckBox(modName, modValue, transformedModifications) {
  const checkboxModValue = getCheckboxModValue(modValue);
  if (checkboxModValue.length) {
    transformedModifications[modName] = checkboxModValue;
  }
}

function buildCateringFormModifications(fields, modifications) {
  return Object.entries(modifications).reduce(
    (transformedModifications, [modName, modValue]) => {
      const { field_type } = fields.find(f => f.slug === modName);

      switch (field_type) {
        case 'free_form':
        case 'quantity':
          transformedModifications[modName] = modValue;
          break;
        case 'checkbox':
          updateCheckBox(modName, modValue, transformedModifications);
          break;
        default:
          throw new Error('unknown field_type');
      }

      return transformedModifications;
    },
    {}
  );
}

/**
 * We get a modifications array from the form values (for Catering and Online ordering) which values consist of [key, value] pair list.
 * Some of those values are no-modifier fields like: 'order-made-for' or 'special-instructions',
 * which is why we filter them out here.
 *
 * @param {*} modifications an array which values consiste of a [key, value] pair list.
 * @returns an array of modifications that only includes modifiers.
 */
const filterNoModifiers = modifications => {
  const noModifiers = [
    'order-made-for',
    'special-instructions',
    'specialInstructions'
  ];

  const modifiers = modifications.filter(
    ([modName, modValue]) => !noModifiers.includes(modName)
  );

  return modifiers;
};

/**
 * Creates a modifiers object based on menu item details form and passes data to the pricePreview endpoint.
 * Needs to trigger api call for all field_types however free form doesn't need to trigger a price preview call.
 * @param {array} fields - array for modifier fields in menu details form
 * @param {object} modifications - object of modified field name and value
 * @return {object} field name and value being passed into API
 */
function buildFormModificationsForPricePreview(fields, modifications) {
  const modifiers = filterNoModifiers(Object.entries(modifications));

  return modifiers.reduce((transformedModifications, [modName, modValue]) => {
    const field = fields.find(f => f.slug === modName);

    if (!field) {
      return transformedModifications;
    }

    switch (field.field_type) {
      case 'free_form':
        break;
      case 'checkbox':
        updateCheckBox(modName, modValue, transformedModifications);
        break;
      case 'quantity':
        transformedModifications[modName] = modValue;
        break;
      default:
        throw new Error('unknown field_type');
    }

    return transformedModifications;
  }, {});
}

/**
 * When the field_type = checkbox
 * Determine whether the field_type should be radio button
 * or checkbox base on the minQty and maxQty property
 * maxQty > 1 || (minQty === 0 && maxQty > 1) -> checkbox
 * minQty > 0 && maxQty === 1 -> radiobutton
 */
const getCheckboxOrRadio = (minQty, maxQty) => {
  minQty = parseInt(minQty);
  maxQty = parseInt(maxQty);

  if (minQty > 0 && maxQty === 1) {
    return RADIO_BUTTON;
  }
  // see https://docs.google.com/document/d/1T0_kHuZRaiXkJbWeVvvduwWRZfa7Tmrv2DQMS6UzIxk/edit#
  // max_qty > 1) || (Min_qty === 0, max_qty  => 1
  return CHECKBOX;
};

const getVariantPrice = price => (price === '0.00' ? '' : price);

const getVariantLabel = (name, calories, caloriesMax) => {
  let labelBuilder = `${name}`;

  if (calories) {
    labelBuilder += `, ${calories}${caloriesMax ? `–${caloriesMax}` : ''} Cal.`;
  }

  return labelBuilder;
};

/**
 * @param {ModifierGroup} modifierGroup
 * @returns {string}
 */
const getDisplayName = modifierGroup => {
  return modifierGroup.display_name || modifierGroup.name || '';
};

const getVariantName = (name, minQty, maxQty) => {
  if (minQty === maxQty) {
    return `${name} (Choose ${minQty})`;
  } else if (minQty > 1 && !maxQty) {
    return `${name} (Choose at least ${minQty})`;
  } else if (minQty === 0 && maxQty > 0) {
    return `${name} (Choose up to ${maxQty})`;
  } else if (minQty > 0 && maxQty > 0) {
    return `${name} (Choose between ${minQty}-${maxQty})`;
  }
};

/**
 * Builds the required field objects that is readable by redux form.
 */
const getTransformedRequiredFieldsForForm = requiredFields => {
  return requiredFields.reduce((acc, current) => {
    if (current.field_type === FREEFORM) {
      acc[current.slug] = current.selected[0].option_text;
    } else if (current.field_type === QUANTITY) {
      const quantitySelection = current.selected.reduce((field, cur) => {
        field[cur.option_slug] = cur.quantity;
        return field;
      }, {});

      acc[current.slug] = quantitySelection;
    } else {
      const fieldType = getCheckboxOrRadio(current.min_qty, current.max_qty);

      if (fieldType === RADIO_BUTTON) {
        acc[current.slug] = current.selected[0].option_slug;
      } else {
        const checkboxSelection = current.selected.reduce((field, cur) => {
          field[cur.option_slug] = true;
          return field;
        }, {});

        acc[current.slug] = checkboxSelection;
      }
    }

    return acc;
  }, {});
};

export {
  buildCateringFormModifications,
  buildFormModificationsForPricePreview,
  getCheckboxOrRadio,
  getTransformedRequiredFieldsForForm,
  getVariantName,
  getVariantPrice,
  getVariantLabel,
  getDisplayName
};
